"use client";
import { useEffect } from "react";
import { useChallenges } from "@/hooks/useLoyaltyExpansion";
import { useMenu } from "@/hooks/useMenu";
import { useCart, type CartLine } from "@/store/cart";

export function ChallengeRewardSyncer() {
  const { completed, loading } = useChallenges();
  const { items: menuItems } = useMenu();
  const { cart, addToCart, removeLine } = useCart();

  useEffect(() => {
    if (loading) return;

    // Helper to get a stable ID for a challenge, in case id is missing in mock data
    const getChallengeId = (c: any, idx: number) => String(c.id ?? c.challengeId ?? c.name ?? `fallback-${idx}`);

    // 1. Remove rewards in the cart that are no longer completed
    const completedChallengeIds = new Set(completed.map((c, i) => getChallengeId(c, i)));
    for (const line of cart) {
      if (line.isReward) {
        // If it doesn't have a source, or the source is no longer completed, remove it
        if (!line.rewardSource || !completedChallengeIds.has(String(line.rewardSource))) {
          removeLine(line.id);
        }
      }
    }

    // 2. Add rewards for newly completed challenges
    if (!completed || completed.length === 0 || !menuItems || menuItems.length === 0) return;

    for (let i = 0; i < completed.length; i++) {
      const challenge = completed[i];

      // Skip if the reward has already been issued by the backend
      if (challenge.rewardIssued) continue;

      const rewardType = challenge.reward?.type || (challenge.reward as any)?.kind;
      if (rewardType === "free_item" && challenge.reward && "itemId" in challenge.reward && challenge.reward.itemId) {
        const rewardItemId = challenge.reward.itemId;
        const rewardQty = (challenge.reward as any).quantity ?? 1;
        const challengeId = getChallengeId(challenge, i);

        // Check if this reward is already in the cart
        const alreadyInCart = cart.some((line) => line.rewardSource === challengeId);
        
        // Check if we've already synced this reward to the cart in this browser
        const hasBeenSynced = typeof window !== "undefined" && window.localStorage.getItem(`synced_reward_${challengeId}`);

        if (!alreadyInCart && !hasBeenSynced) {
          const menuItem = menuItems.find((item) => String(item.id) === String(rewardItemId));
          if (menuItem) {
            const line: CartLine = {
              id: `reward-${challengeId}-${Date.now()}`,
              itemId: menuItem.id,
              name: menuItem.name,
              categoryId: menuItem.cat,
              categoryName: menuItem.categoryName,
              categoryNameRaw: menuItem.categoryNameRaw,
              price: 0, // Free item
              qty: rewardQty,
              tone: menuItem.tone || "neutral",
              selectedModifiers: [],
              selectedVariants: [],
              modsLabel: [],
              notes: "",
              isReward: true,
              rewardSource: challengeId,
              imageUrl: menuItem.imageUrl,
            };
            
            addToCart(line);
            
            // Mark as synced so we don't re-add it if the user clears the cart (e.g. after ordering)
            if (typeof window !== "undefined") {
              window.localStorage.setItem(`synced_reward_${challengeId}`, "true");
            }
          }
        }
      }
    }
  }, [completed, loading, menuItems, cart, addToCart, removeLine]);

  return null;
}
